import { traceName } from './runLog.js'
import { createInjector, spinFor } from './injector.js'

// Runs a labeled run:
//   arm()      called once the debug screen is unmounted; waits ARM_DELAY_MS
//   [flash]    optional full-screen white for FLASH_FRAMES frames (video sync);
//              video-sync runs also show the frame strobe while recording
//   record     the logger takes performance.now() every ticker callback while
//              the beat plays; the native layer opens the run flag and the
//              Android trace section
//   stop       when the beat ends (single) or the duration is up (sustained)
//   finalize   after the last frame: close the native side, hand the result on
//
// frameTick() is the per-frame hot path: no allocation, no logging, no DOM.
// Platform-free: the native plugin (Gate1) and the stage are passed in.

export const ARM_DELAY_MS = 2000
export const FLASH_FRAMES = 6
const FLASH_HOLD_AFTER_MS = 300

const IDLE = 0, ARMING = 1, PREROLL = 2, RECORDING = 3

export function createRunController({ logger, build, native, now = () => performance.now(), spin = ms => spinFor(ms, now), onFinished }) {
    let phase = IDLE
    let injector = null
    let stage = null
    let beat = null
    let cfg = null
    let run = null
    let prerollFrames = 0
    let armTimer = null

    function frameTick() {
        if (phase < PREROLL) return
        const t = now()
        if (phase === PREROLL) {
            if (prerollFrames > 0) { prerollFrames--; return }
            stage.flash.visible = false
            beginRecording(t)
        }
        logger.record(t)
        if (cfg.flash) stage.strobe.advance()
        if (injector) injector.tick(t)
        const sustained = cfg.mode === 'sustained'
        const elapsed = t - run.perfStartMs
        if (beat.step(t)) {
            run.beatsCompleted++
            if (sustained && elapsed < cfg.durationMs) beat.start(t)
            else finish(t, 'complete')
        } else if (sustained && elapsed >= cfg.durationMs) {
            finish(t, 'complete')
        }
    }

    function beginRecording(t) {
        phase = RECORDING
        logger.start(t)
        run.perfStartMs = t
        run.startedAtEpochMs = performance.timeOrigin + t
        beat.start(t)
        if (cfg.flash) stage.strobe.show(true)
        const before = now()
        run.nativeBegin = native.runBegin({ label: cfg.label, traceName: run.traceName, cookie: run.cookie })
            .then(r => ({ ...r, callPerfMs: before, resolvedPerfMs: now() }))
            .catch(e => ({ error: String(e) }))
    }

    function finish(t, status) {
        logger.stop(t)
        phase = IDLE
        run.perfStopMs = t
        run.status = status
        const before = now()
        run.nativeEnd = native.runEnd({ cookie: run.cookie })
            .then(r => ({ ...r, callPerfMs: before, resolvedPerfMs: now() }))
            .catch(e => ({ error: String(e) }))
        if (cfg.flash) stage.strobe.show(false)
        if (injector) { injector.end(); run.inject = injector.summary(); injector = null }
        if (cfg.flash && status === 'complete') {
            stage.flash.visible = true
            setTimeout(() => { stage.flash.visible = false }, FLASH_HOLD_AFTER_MS)
        }
        beat.reset()
        setTimeout(finalize, 0)
    }

    async function finalize() {
        const r = run
        const [nativeBegin, nativeEnd] = await Promise.all([r.nativeBegin ?? null, r.nativeEnd ?? null])
        run = null
        onFinished?.({ ...r, nativeBegin, nativeEnd, cfg })
    }

    return {
        frameTick,
        get busy() { return phase !== IDLE },

        attach(s, b) { stage = s; beat = b },

        // cfg: { label, mode: 'single'|'sustained', durationMs, flash, inject? }
        // inject (test only): { type: 'stall'|'gpu', every, stallMs | burstFrames, layers }
        arm(runCfg, { debugUnmountedAtPerfMs }) {
            if (phase !== IDLE) throw new Error('a run is already in progress')
            cfg = runCfg
            if (cfg.inject?.type === 'gpu') stage.overload.setLayers(cfg.inject.layers)
            injector = createInjector(cfg.inject, { overload: stage.overload, spin })
            phase = ARMING
            run = {
                label: cfg.label,
                traceName: traceName({ label: cfg.label, commit: build.commit, buildType: build.buildType, mode: cfg.mode }),
                cookie: (Date.now() % 2147483647) | 0,
                armedAtPerfMs: now(),
                debugUnmountedAtPerfMs,
                beatsCompleted: 0,
                perfStartMs: null,
                perfStopMs: null,
                status: null,
                inject: cfg.inject && cfg.inject.type !== 'none' ? { ...cfg.inject, count: 0, frameTimesMs: [] } : null,
            }
            armTimer = setTimeout(() => {
                armTimer = null
                prerollFrames = cfg.flash ? FLASH_FRAMES : 0
                if (cfg.flash) stage.flash.visible = true
                phase = PREROLL
            }, ARM_DELAY_MS)
        },

        // Paused/backgrounded (native 'runAborted', or a hidden browser tab).
        abort(reason) {
            if (phase === IDLE) return
            if (armTimer) { clearTimeout(armTimer); armTimer = null }
            run.abortReason = reason
            if (phase === RECORDING) {
                finish(now(), 'aborted')
            } else {
                stage.flash.visible = false
                injector = null
                phase = IDLE
                run.status = 'aborted'
                setTimeout(finalize, 0)
            }
        },
    }
}
